// LanguaLUtils.tsx
import { fetchLanguaLChildren, fetchLanguaLParents } from '@/api/langual'
export type LanguaLID = string

export interface LanguaLDescriptor {
  id: LanguaLID
  description: string
  translatedDescription?: string
  childrenDescriptors?: Array<LanguaLDescriptor>
  parentDescriptor?: LanguaLDescriptor

  // Some APIs may return "children" instead of "childrenDescriptors" in parent lookups
  children?: Array<LanguaLDescriptor>
}

type ParentChain = Array<LanguaLDescriptor>

let childrenCache = new Map<string, Array<LanguaLDescriptor>>()
let parentChainCache = new Map<string, ParentChain>()

async function fetchLanguaLChildrenCached(id: LanguaLID): Promise<Array<LanguaLDescriptor>> {
  if (childrenCache.has(id)) return childrenCache.get(id)!
  const children = await fetchLanguaLChildren(id)
  childrenCache.set(id, children)
  return children
}

async function getAllDescriptorParentInfoCached(languaLID: LanguaLID): Promise<ParentChain> {
  if (parentChainCache.has(languaLID)) return parentChainCache.get(languaLID)!

  const parents: Array<any> = []
  async function load(id: LanguaLID): Promise<ParentChain> {
    const { parentDescriptor, ...parent } = await fetchLanguaLParents(id)
    parents.push(parent)
    if (parentDescriptor && parentDescriptor.id === '00000') {
      return parents
    }
    await load(parentDescriptor.id)
    return parents
  }

  const parentChain = await load(languaLID)
  parentChainCache.set(languaLID, parentChain)
  parents.reverse()

  return parents
}

function _getUniqueDescriptors(descriptors: Array<LanguaLDescriptor>): Array<LanguaLDescriptor> {
  const found = new Set<string>()
  const unique: Array<LanguaLDescriptor> = []
  descriptors.forEach((d) => {
    if (!found.has(d.id)) {
      unique.push(d)
      found.add(d.id)
    }
  })
  return unique
}

async function mergeWithDescriptorChildren(
  rootDescriptors: Array<LanguaLDescriptor>,
  parentsToMergeChildren: Array<LanguaLDescriptor>,
): Promise<Array<LanguaLDescriptor>> {
  function updateChildrenOrSelf(
    nodes: Array<LanguaLDescriptor>,
    children: Array<LanguaLDescriptor>,
    descriptor: LanguaLDescriptor,
  ): Array<LanguaLDescriptor> {
    return nodes.map((d) =>
      d.id === descriptor.id
        ? { ...d, childrenDescriptors: d.children || children }
        : {
            ...d,
            childrenDescriptors:
              d.childrenDescriptors &&
              updateChildrenOrSelf(d.childrenDescriptors, children, descriptor),
          },
    )
  }

  let fullMerge = rootDescriptors
  const allChildren = await Promise.all(
    parentsToMergeChildren.map((descriptor) =>
      descriptor.childrenDescriptors
        ? Promise.resolve(descriptor.childrenDescriptors)
        : fetchLanguaLChildrenCached(descriptor.id),
    ),
  )

  allChildren.forEach((children, index) => {
    fullMerge = updateChildrenOrSelf(fullMerge, children, parentsToMergeChildren[index])
  })

  return fullMerge
}

async function loadLeafParentsOnRootDescriptors(
  languaLIDs: Array<LanguaLID>,
  rootDescriptors: Array<LanguaLDescriptor>,
): Promise<{
  rootDescriptors: Array<LanguaLDescriptor>
  expandedParents: Array<LanguaLDescriptor>
}> {
  const loadedChains = await Promise.all(
    languaLIDs.map((id) => getAllDescriptorParentInfoCached(id)),
  )

  const flattened = loadedChains.reduce<Array<LanguaLDescriptor>>((a, b) => a.concat(b), [])
  const uniqueParents = _getUniqueDescriptors(flattened)
  const merged = await mergeWithDescriptorChildren(rootDescriptors, uniqueParents)
  return { rootDescriptors: merged, expandedParents: uniqueParents }
}

function findDescriptorByID(
  languaLID: LanguaLID,
  descriptors: Array<LanguaLDescriptor>,
): LanguaLDescriptor | null {
  for (const d of descriptors) {
    if (d.id === languaLID) return d
    if (d.childrenDescriptors && d.childrenDescriptors.length > 0) {
      const found = findDescriptorByID(languaLID, d.childrenDescriptors)
      if (found) return found
    }
  }
  return null
}

function getDescriptionById(languaLID: LanguaLID, descriptors: Array<LanguaLDescriptor>): string {
  const d = findDescriptorByID(languaLID, descriptors)
  return capitalizeText(d?.description ?? '')
}

function capitalizeText(text: string): string {
  const t = text.toLowerCase()
  return t.charAt(0).toUpperCase() + t.slice(1)
}

const LanguaLUtils = {
  loadLeafParentsOnRootDescriptors,
  getAllDescriptorParentInfo: getAllDescriptorParentInfoCached,
  mergeWithDescriptorChildren,
  _getUniqueDescriptors,
  findDescriptorByID,
  getDescriptionById,
  capitalizeText,
  // exposed for the selector to opportunistically warm caches if desired
  _fetchLanguaLChildrenCached: fetchLanguaLChildrenCached,
}

export default LanguaLUtils
